<template>
  <DashboardCard
    :title="title"
    :count="totalRows"
    :loading="loading"
    :widget-type="widgetType"
    :expanded="expanded"
    class="sc-open-invoices"
    @update:expanded="$emit('update:expanded', $event)"
  >
    <template #title-append>
      <v-menu v-if="selectedKeys.length" location="bottom start">
        <template #activator="{ props }">
          <v-btn
            variant="text"
            border
            :size="$vuetify.display.xs ? 'small' : undefined"
            class="ml-2 pr-2"
            v-bind="props"
          >
            <span>Actions</span>
            <v-divider v-if="$vuetify.display.mdAndUp" vertical class="ml-3 mr-1" />
            <v-icon>mdi-menu-down</v-icon>
          </v-btn>
        </template>
        <v-list class="pa-0">
          <v-progress-linear v-if="exportLoading" color="primary" indeterminate />
          <v-list-item data-track="report.open-invoices.export" @click="exportHandler">
            <div>
              <v-list-item-title>Export selected invoices</v-list-item-title>
            </div>
            <template #append>
              <v-icon>mdi-file-export</v-icon>
            </template>
          </v-list-item>
          <AppTooltip text="You must select only 1 record to resend an invoice">
            <template #default="{ props }">
              <v-list-item
                v-bind="props"
                :class="{ 'dashboard-card__action-item--disabled': selectedKeys.length !== 1 }"
                class="dashboard-card__action-item"
                data-track="report.open-invoices.resend"
                @click="sendInvoiceHandler"
              >
                <div>
                  <v-list-item-title>Resend Invoice</v-list-item-title>
                </div>
                <template #append>
                  <v-icon :disabled="selectedKeys.length !== 1">mdi-email-send</v-icon>
                </template>
              </v-list-item>
            </template>
          </AppTooltip>
        </v-list>
      </v-menu>
    </template>
    <template #actions>
      <v-menu
        v-if="$vuetify.display.mdAndDown"
        v-model="filtersMenu"
        location="start"
        :close-on-content-click="false"
      >
        <template #activator="{ props }">
          <v-btn v-bind="props" icon>
            <v-icon>mdi-filter</v-icon>
          </v-btn>
        </template>
        <v-card flat>
          <v-card-title class="py-1">
            <span>{{ title }} Filters</span>
            <v-spacer />
            <v-btn icon @click="filtersMenu = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
        </v-card>
        <v-divider />
        <v-card class="pa-2 d-flex gap-3 justify-center flex-column">
          <v-switch
            v-model="excludePaid"
            label="Exclude Paid"
            class="mt-0 ml-1"
            hide-details
          />
          <OpenInvoicesDateFilter
            v-model:date-start="date_start"
            v-model:date-end="date_end"
            @fetch="fetchWrapper"
          />
          <v-text-field
            v-model="keyword"
            placeholder="Invoice № or Deal name"
            hide-details
            clearable
          />
          <v-select
            v-if="isAdmin"
            label="Select Agent"
            hide-details
            :items="members"
            item-title="full_name"
            item-value="id"
            clearable
            @update:model-value="changeUser"
          />
          <v-btn color="primary" @click="filtersMenu = false">Apply</v-btn>
        </v-card>
      </v-menu>
      <div v-else class="d-flex align-center gap-2">
        <v-switch
          v-model="excludePaid"
          label="Exclude Paid"
          class="mt-0"
          hide-details
        />
        <OpenInvoicesDateFilter
          v-model:date-start="date_start"
          v-model:date-end="date_end"
          @fetch="fetchWrapper"
        />
        <v-text-field
          v-model="keyword"
          placeholder="Invoice № or Deal name"
          hide-details
          clearable
        />
        <v-select
          v-if="isAdmin"
          label="Select Agent"
          hide-details
          :items="members"
          item-title="full_name"
          item-value="id"
          style="max-width: 200px"
          clearable
          @update:model-value="changeUser"
        />
      </div>
    </template>
    <template #content>
      <v-card flat>
        <v-overlay
          class="align-center justify-center"
          :model-value="loading"
          :opacity=".7"
          contained
          scrim="surface"
        >
          <div class="text-center">
            <v-progress-circular
              :size="50"
              color="primary"
              indeterminate
            />
          </div>
        </v-overlay>
        <OpenInvoicesTable
          v-model="tableOptions"
          v-model:selected-keys="selectedKeys"
          :items="items"
          :filter-loading="filterLoading && !loading"
          :server-items-length="totalRows"
        />
      </v-card>
      <CommonSidePanel
        v-model="sendInvoiceDrawer"
        :width="600"
        persistent
      >
        <SendInvoiceForm
          v-if="sendInvoiceDrawer"
          :invoice="selectedInvoice"
          :deal="selectedInvoice.deal"
          @cancel="sendInvoiceDrawer = false"
          @refresh="submitInvoiceHandler"
        />
      </CommonSidePanel>
    </template>
  </DashboardCard>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { mapActions, mapState } from 'vuex';
import { debounce } from 'lodash-es';

import { UserWidgetsEnum, UserWidgetsLabels } from '@/bundles/Settings/enums/userLayoutEnum';
import { Nullable, ValueOf } from '@/utils/types';
import { ServiceFactory } from '@/services/ServiceFactory';
import { ITableOptions } from '@/bundles/BaseTable/interfaces';
import { getInvoiceAging, getInvoiceStatusProps } from '@/bundles/Deal/helpers';
import { ExportRecordType, ExportType } from '@/bundles/Common/types';
import Member from '@/bundles/Members/models/Member';
import { StoreModulesNamesEnum } from '@/store/StoreModulesNamesEnum';
import { LOAD_ACTIVE_MEMBERS } from '@/store/modules/members/members-types';
import { getTableOptions } from '@/bundles/BaseTable/helpers/consts';
import { INameValue } from '@/types/common';
import { RequestAbortMixin } from '@/bundles/App/mixins/RequestAbortMixin';
import { isRequestCancelledError } from '@/bundles/Http/mappers/ErrorMapper';
import { useNotifications } from '@/bundles/App/composables/useNotifications';
import { useUserRoles } from '@/bundles/App/composables/useUserRoles';

import DashboardCard from '@/bundles/Dashboard/components/DashboardCard.vue';
import OpenInvoicesTable from '@/bundles/Invoice/components/OpenInvoicesTable.vue';
import OpenInvoicesDateFilter from '@/bundles/Dashboard/components/filters/OpenInvoicesDateFilter.vue';
import SendInvoiceForm from '@/components/invoice/SendInvoiceForm.vue';
import AppTooltip from '@/bundles/App/components/common/AppTooltip.vue';
import CommonSidePanel from '@/bundles/Common/components/panels/CommonSidePanel.vue';

type MemberWithFullName = Member & { full_name: string };

interface IDashboardOpenInvoicesCardData {
  items: any[];
  loading: boolean;
  filterLoading: boolean;
  widgetType?: UserWidgetsEnum;
  exportLoading: boolean;
  date_start: Nullable<string>;
  date_end: Nullable<string>;
  title?: ValueOf<keyof typeof UserWidgetsLabels>;
  members: MemberWithFullName[];
  memberId: Nullable<string>;
  filtersMenu: boolean;
  tableOptions: ITableOptions;
  selectedKeys: string[];
  keyword: string;
  excludePaid: boolean;
  sendInvoiceDrawer: boolean;
  selectedInvoice: Nullable<Record<string, any>>;
  totalRows: number;
}

const InvoiceService = ServiceFactory.get('invoice');
const SavedSetService = ServiceFactory.get('savedSets');

export default defineComponent({
  name: 'DashboardOpenInvoicesCard',

  components: {

    CommonSidePanel,
    AppTooltip,
    SendInvoiceForm,
    OpenInvoicesDateFilter,
    DashboardCard,
    OpenInvoicesTable,
  },
  mixins: [RequestAbortMixin],

  props: {
    expanded: Boolean,
  },

  emits: ['update:expanded'],

  setup () {
    const { addNotification } = useNotifications();
    const { isAdmin } = useUserRoles();

    return { addNotification, isAdmin };
  },

  data: (): IDashboardOpenInvoicesCardData => ({
    items: [],
    loading: true,
    filterLoading: false,
    exportLoading: false,
    date_start: null,
    date_end: null,
    members: [],
    memberId: null,
    filtersMenu: false,
    tableOptions: getTableOptions({
      sortBy: ['daysDiff'],
      sortDesc: [true],
      itemsPerPage: 10,
      itemsPerPageOptions: [
        { title: '10', value: 10 },
        { title: '25', value: 25 },
        { title: '50', value: 50 },
        { title: '100', value: 100 },
        { title: 'All', value: -1 }
      ]
    }),
    selectedKeys: [],
    keyword: '',
    excludePaid: true,
    sendInvoiceDrawer: false,
    selectedInvoice: null,
    totalRows: 0,
  }),

  computed: {
    ...mapState({
      token: (state: any) => state.idToken,
    }),
  },

  watch: {
    keyword (newValue, oldValue) {
      if (newValue !== oldValue) {
        this.resetPaginationDebounced(this);
      }
    },
    excludePaid () {
      this.resetPaginationDebounced(this);
    },
    tableOptions: {
      handler () {
        this.fetchWrapper();
      },
      deep: true,
    },
    'tableOptions.page' () {
      this.selectedKeys = [];
    }
  },

  created () {
    this.widgetType = UserWidgetsEnum.open_invoices;
    this.title = UserWidgetsLabels[UserWidgetsEnum.open_invoices];

    this.fetchWrapper();

    // @ts-ignore
    if (this.isAdmin) {
      this.getMembers();
    }
  },

  methods: {
    ...mapActions(StoreModulesNamesEnum.Member, {
      loadActiveMembers: LOAD_ACTIVE_MEMBERS,
    }),
    resetPaginationDebounced: debounce(function (vm: any) {
      vm.resetPagination();
    }, 300),
    async fetchWrapper () {
      try {
        this.filterLoading = true;

        const params: INameValue<any>[] = [
          { name: 'exclude_paid', value: this.excludePaid },
          { name: 'pageOffset', value: this.tableOptions.itemsPerPage * (this.tableOptions.page - 1) },
          { name: 'pageLimit', value: this.tableOptions.itemsPerPage === -1 ? this.totalRows : this.tableOptions.itemsPerPage },
          { name: 'sort', value: this.tableOptions.sortBy },
          { name: 'descending', value: this.tableOptions.sortDesc },
        ];

        if (this.date_start) {
          params.push({ name: 'date_start', value: this.date_start })
        }
        if (this.date_end) {
          params.push({ name: 'date_stop', value: this.date_end });
        }
        // @ts-ignore
        if (this.isAdmin && this.memberId) {
          params.push({ name: 'member', value: this.memberId });
        }
        if (this.keyword) {
          params.push({ name: 'keyword', value: encodeURIComponent(this.keyword) });
        }

        this.handleRequestAbort();
        const { data } = await InvoiceService.getOpenInvoices({
          token: this.token,
          params,
          controller: this.abortController,
        });

        this.items = data.data.map(item => {
          const { status, color } = getInvoiceStatusProps(item);
          const { daysDiff, diffLabel } = getInvoiceAging(item.due_date);
          return {
            ...item,
            status,
            color,
            daysDiff,
            diffLabel
          };
        });
        this.totalRows = data.total;
        this.filterLoading = false;
        this.loading = false;
      } catch (error: any) {
        this.addNotification(error);
        if (!isRequestCancelledError(error)) {
          this.filterLoading = false; // can't be inside finally because fetchWrapper calls several times when changing table options
          this.loading = false;
        }
      }
    },
    async exportHandler () {
      try {
        this.exportLoading = true;

        const payload = {
          record_type: ExportRecordType.open_invoices,
          type: ExportType.list_export,
          records: this.selectedKeys,
          date_start: this.date_start || null,
          date_stop: this.date_end || null,
        };

        if (this.tableOptions.sortBy?.length) {
          Object.assign(payload, { sort: this.tableOptions.sortBy[0] });
        }

        await SavedSetService.exportToExcelNew(payload);
        this.addNotification({
          status: 200,
          title: 'Success',
          message: 'Your export <b>is being generated</b>. When <b>complete</b>, a notification <b>will appear</b> at the <b>top right</b> of the screen. ' +
            'Some exports take a few minutes to generate.',
        });
      } catch (error: any) {
        this.addNotification({ ...error });
      } finally {
        this.exportLoading = false;
      }
    },
    changeUser (value) {
      this.memberId = value;
      this.fetchWrapper();
    },
    async getMembers () {
      const data = await this.loadActiveMembers();

      const members = data.map((item) => {
        return {
          ...item,
          full_name: `${item.profile.first_name} ${item.profile.last_name}`
        }
      });
      members.sort((a, b) => (a.profile.last_name > b.profile.last_name ? 1 : -1))
      this.members = members;
    },
    sendInvoiceHandler () {
      if (this.selectedKeys.length !== 1) return;

      this.selectedInvoice = this.items.find(item => item._key === this.selectedKeys[0]);
      this.sendInvoiceDrawer = true;
    },
    submitInvoiceHandler () {
      this.sendInvoiceDrawer = false;
      this.selectedInvoice = null;
    },
    resetPagination () {
      if (this.tableOptions.page === 1) {
        this.fetchWrapper();
      } else {
        this.tableOptions.page = 1;
      }
    },
  }
});
</script>

<style lang="scss" scoped>
.dashboard-card__action-item--disabled {
  color: rgba(var(--v-theme-on-surface), var(--v-disabled-opacity)) !important;
}
</style>
